import React, { useMemo, useState } from 'react'
import { KibTabsNew, KibTabsNewItem } from '@chewy/kib-content-groups-react'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibPaginationNew } from '@chewy/kib-menus-react'
import classNames from 'classnames'
import { RhapTable } from '@pbt/pbt-ui-components'
import type {
  RhapTableProps,
  RowData,
} from '@pbt/pbt-ui-components/src/components/tables/RhapTable'

import type { WidgetColumn, WidgetColumnRow } from '~/types'

import '@chewy/kib-menus-styles/dist/styles/kib-pagination-new.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-content-groups-styles/dist/styles/kib-tabs-new.css'
import styles from './PatientHistoryWidget.module.scss'

/**
 * Configuration for a single tab in the PatientHistoryWidget
 * @template T - The type of data rows extending WidgetColumnRow
 */
export interface TabData<T extends WidgetColumnRow> {
  /** Column definitions specific to this tab */
  columns: WidgetColumn<T>[]
  /** Data items to display in this tab */
  data: T[]
  /** Unique identifier for the tab */
  id: number
  /** Display label for the tab */
  label: string
  /** Optional count badge to show next to the tabs label */
  labelCount?: number
  /** Component to show when this tab has no data */
  noRecords?: React.ReactNode
  /** Total number of items across all pages (required for pagination) */
  totalCount?: number
}

/**
 * Configuration for a header action button
 */
export interface HeaderAction {
  /** Callback function when the action button is clicked */
  onClick: () => void
  /** Text to display on the action button */
  text: string
}

export interface PatientHistoryWidgetProps<T extends WidgetColumnRow> {
  /** Optional className to apply to the widget container */
  className?: string
  /** Column definitions for the table */
  columns?: WidgetColumn<T>[]
  /** Current page number for pagination (required if using pagination) */
  currentPage?: number
  /** Array of data items to display in the table */
  data?: T[]
  /** Header action button or custom React element */
  headerAction?: HeaderAction | React.ReactNode
  /** Loading state for the table */
  isLoading?: boolean
  /** Layout options passed to the underlying RhapTable */
  layoutOptions?: RhapTableProps['layoutOptions']
  /** Number of rows to show per page (default: 5). Must remain constant - dynamic row counts would require refactoring this pagination logic */
  maxRows?: number
  /** Component to show when no data is available */
  noRecords?: React.ReactNode
  /** Callback fired when page changes (required if using pagination) */
  onPageChange?: (page: number) => void
  /** Callback fired when tab selection changes */
  onSelectedTabChange?: (tabId: number) => void
  /** Currently selected tab index (default: 0) */
  selectedTab?: number
  /** Tab configuration for multi-tab display */
  tabs?: TabData<T>[]
  /** Widget title displayed in header */
  title?: string
  /** Total number of items across all pages (required for pagination) */
  totalCount?: number
}

/**
 * Reusable widget for displaying patient history data with tabs and server-side pagination.
 *
 * Features: tabbed interface, loading/empty states, header actions, consumer-driven pagination.
 * Consumer handles data fetching; widget handles UI and delegates pagination/tab events.
 *
 * @template T - The type of data rows extending WidgetColumnRow
 *
 * @example
 * ```tsx
 * // With pagination
 * <PatientHistoryWidget
 *   data={prescriptions} // Already paginated from server
 *   currentPage={currentPage}
 *   totalCount={totalCount}
 *   onPageChange={handlePageChange}
 * />
 *
 * // With tabs
 * <PatientHistoryWidget
 *   tabs={[
 *     { id: 0, label: "Active", data: activeProblems, columns: problemColumns }
 *   ]}
 * />
 * ```
 */
const PatientHistoryWidget = <T extends WidgetColumnRow>({
  title,
  data,
  columns,
  totalCount,
  isLoading = false,
  noRecords,
  tabs,
  selectedTab = 0,
  onSelectedTabChange,
  headerAction,
  maxRows = 5,
  layoutOptions = {},
  currentPage,
  onPageChange,
  className,
}: PatientHistoryWidgetProps<T>) => {
  const [internalSelectedTab, setInternalSelectedTab] = useState(selectedTab)

  const currentTab = tabs?.[internalSelectedTab]
  const currentData = tabs ? currentTab?.data : data
  const currentColumns = tabs ? currentTab?.columns : columns
  const currentNoRecords = tabs ? currentTab?.noRecords : noRecords
  const currentTotalCount = tabs ? currentTab?.totalCount : totalCount
  const isShowPagination =
    currentTotalCount !== undefined &&
    currentTotalCount > maxRows &&
    currentPage &&
    onPageChange

  const { columns: rhapColumns, rows: rhapRows } = useMemo(() => {
    if (!currentColumns || !currentData) {
      return { columns: [], rows: [] }
    }

    const hasGrowableColumns = currentColumns.some((col) => col.allowRowGrowth)

    const memoColumns = currentColumns.map((column) => ({
      header: column.label || column.header || '',
      width: column.width ? column.width * 100 : undefined,
      fixedWidth: column.fixedWidth,
      renderCell: (rowData: RowData) => {
        const cellContent = (() => {
          if (column.component) {
            const Component = column.component
            return <Component {...(rowData as T)} />
          }

          if (column.prop) {
            const value =
              typeof column.prop === 'function'
                ? column.prop(rowData as T)
                : (rowData as T)[column.prop]
            return value || ''
          }

          if (column.renderCell) {
            return column.renderCell(rowData)
          }

          return ''
        })()

        // Wrap content based on allowRowGrowth setting
        if (column.allowRowGrowth) {
          return <div className={styles.growableCell}>{cellContent}</div>
        }
        if (column.clamp) {
          return (
            <div className={styles.clampedCell} data-clamp={column.clamp}>
              {cellContent}
            </div>
          )
        }

        return cellContent
      },
      clamp: column.clamp,
      isAction: column.isAction,
      disableEllipsis: column.disableEllipsis,
    }))

    // Consumer provides the exact data to display (already paginated from server)
    const memoRows = currentData.map(
      (item) =>
        ({
          ...item,
          className: hasGrowableColumns ? 'has-growable-cells' : undefined,
        }) as RowData,
    )

    return { columns: memoColumns, rows: memoRows }
  }, [currentColumns, currentData, internalSelectedTab])

  const handleTabChange = (tabId: number) => {
    setInternalSelectedTab(tabId)
    onSelectedTabChange?.(tabId)
  }

  const tableContent = (
    <div data-patient-history-widget className={styles.content}>
      {currentData &&
      currentData.length === 0 &&
      currentNoRecords &&
      !isLoading ? (
        <div className={styles.noRecords}>{currentNoRecords}</div>
      ) : (
        <RhapTable
          columns={rhapColumns}
          isLoading={isLoading}
          layoutOptions={!isLoading ? layoutOptions : undefined}
          rows={rhapRows}
        />
      )}
    </div>
  )

  return (
    <div className={classNames(styles.widgetContainer, className)}>
      {title && (
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h2 className={styles.title} data-testid="title">
              {title}
            </h2>
            {headerAction &&
              (React.isValidElement(headerAction) ? (
                headerAction
              ) : (
                <KibButtonNew
                  className={styles.headerAction}
                  emphasis="tertiary"
                  size="small"
                  theme="utility"
                  onClick={(headerAction as HeaderAction).onClick}
                >
                  {(headerAction as HeaderAction).text}
                </KibButtonNew>
              ))}
          </div>
        </div>
      )}
      {tabs && tabs.length > 0 && (
        <div className={styles.tabs}>
          <KibTabsNew
            selected={internalSelectedTab}
            onTabChange={(_, tabId) => handleTabChange(tabId || 0)}
          >
            {tabs.map((tab) => (
              <KibTabsNewItem
                className={styles.tabsContent}
                key={`${tab.label}-${tab.id}`}
                label={tab.label}
                labelCount={tab.labelCount}
              >
                {tableContent}
              </KibTabsNewItem>
            ))}
          </KibTabsNew>
        </div>
      )}
      {!tabs && <div>{tableContent}</div>}
      {isShowPagination && (
        <div className={styles.footer}>
          <KibPaginationNew
            className={styles.pagination}
            current={currentPage}
            total={Math.ceil(currentTotalCount / maxRows)}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  )
}

export default PatientHistoryWidget
export { styles as patientHistoryWidgetStyles }
